import { useEffect, useState } from "react";
import type * as React from "react";
import { Box, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import PageTitle from "utils/PageTitle";
import { openGLSnackbar } from "store/reducers/snackbar/openGLSnackbarSlice";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";
import AccountSecurityCallout from "components/v3/pages/account/AccountManagementPage/AccountSecurityCallout";
import BreadcrumbTrail from "components/v3/sharedComponents/BreadcrumbTrail";
import { PATHS } from "constants/pathConstants";
import CobraDisclaimerModal from "./CobraDisclaimerModal";
import PlanSummaryCard from "./PlanSummaryCard";
import SpouseInviteCard from "./SpouseInviteCard";
import TellOthersGenomicLifeCard from "./TellOthersGenomicLifeCard";
import RequestCancellationRefundSection from "./RequestCancellationRefundSection";

type PlanInformationLocationState = {
  membershipRenewed?: boolean;
  membershipReactivationFailed?: boolean;
};

const PlanInformationPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [cobraModalOpen, setCobraModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const state = location.state as PlanInformationLocationState | undefined;
    if (state?.membershipRenewed) {
      dispatch(
        openGLSnackbar({
          variant: "success",
          header: "",
          subText: "Your membership has been renewed!"
        })
      );
      history.replace(PATHS.ACCOUNT_MANAGEMENT_PAYMENT_INFORMATION);
      return;
    }
    if (state?.membershipReactivationFailed) {
      dispatch(
        openGLSnackbar({
          variant: "error",
          header: "",
          subText: `Your payment seems to have worked, but something went wrong updating your account status. Please log in again, if that doesn't help, please reach out to us!.`
        })
      );
      history.replace(PATHS.ACCOUNT_MANAGEMENT_PAYMENT_INFORMATION);
    }
  }, [dispatch, history, location.state]);

  PageTitle("Plan information");

  const openCobraModal = () => setCobraModalOpen(true);
  const closeCobraModal = () => setCobraModalOpen(false);

  return (
    <GradientFramedLayout showHeader={true}>
      <CobraDisclaimerModal open={cobraModalOpen} onClose={closeCobraModal} />

      <Box
        sx={{
          alignItems: "stretch",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          marginX: "auto",
          maxWidth: { md: 840, xs: "100%" },
          minWidth: 0,
          paddingBottom: "40px",
          paddingTop: { md: 3, xs: 2 },
          paddingX: { md: 3, xs: 2 },
          width: "100%"
        }}
      >
        <Typography
          variant="h4_primary"
          component="h1"
          sx={{ fontWeight: 700, textAlign: "left", width: "100%" }}
        >
          Plan Information
        </Typography>

        <BreadcrumbTrail
          segments={[
            { label: "Account", to: PATHS.ACCOUNT_MANAGEMENT },
            { label: "Plan Information" }
          ]}
        />

        <PlanSummaryCard onReactivateClick={openCobraModal} />

        <SpouseInviteCard />

        <TellOthersGenomicLifeCard onOpenCobraModal={openCobraModal} />

        <RequestCancellationRefundSection />

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            maxWidth: "100%",
            minWidth: 0,
            width: "100%"
          }}
        >
          <Box
            sx={{
              boxSizing: "border-box",
              maxWidth: { md: "66%", xs: "100%" },
              minWidth: 0,
              width: { md: "66%", xs: "100%" }
            }}
          >
            <AccountSecurityCallout />
          </Box>
        </Box>
      </Box>
    </GradientFramedLayout>
  );
};

export default PlanInformationPage;
